import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ChevronLeft, ShieldCheck } from 'lucide-react';
import { useEffect, useRef, useState, Fragment } from 'react';
import type { ReactNode } from 'react';
import { useAppStore } from '../../app/appStore';
import { useAuthStore } from '../../features/auth/authStore';
import { fetchMilharMineStats, fetchMilharStats } from '../../features/milhar/api';
import type { MilharHistoryItem } from '../../features/milhar/api';
import { MilharVerifyOverlay } from '../../features/milhar/MilharVerifyOverlay';
import { RoundDetailSheet } from '../../features/milhar/RoundDetailSheet';
import { useCountryStore } from '../../shared/country/countryStore';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { fmtMoney } from '../../shared/lib/format';
import { HistoryLegend } from '../../shared/ui/HistoryLegend';
import { historyLegendView } from '../../shared/game/historyLegend';
import { groupByLocalDay } from '../../shared/milhar/historyDays';
import { hasMineData, mineMark } from '../../shared/milhar/historyRowView';
import { historyTimeParts, isSameLocalDay } from '../../shared/milhar/historyTime';
import {
  DEFAULT_STATS_TAB, gapText, STATS_TABS, statsRows, toggleSorted, type StatsTab,
} from '../../shared/milhar/statsView';
import type { MilharTierId } from '../../shared/milhar/tiers';

/**
 * 统计页。视觉规格 §12A。
 *
 * <p>三条不能违反的：
 * <ol>
 *   <li>**冷热不用颜色编码** —— 只用排序位置和柱长。满屏红蓝是路单的视觉套路，
 *       中性灰阶更像数据而不是预言</li>
 *   <li>**位选择 chip 是一等公民**，不能藏进筛选器 —— Milhar 是位值制游戏，
 *       D1 和 D4 的分布是互相独立的四件事</li>
 *   <li>页脚放**事实陈述**，不放 `Cada rodada é independente`。那句是对遗漏值的
 *       直接反驳，贴在工具旁会读作免责声明，用户的结论会是「那句走过场，工具才是
 *       真的」，反而比不写更糟。完整教育留在 §13（master §9.3.5）</li>
 * </ol>
 */
export function MilharStatsPage({ tier = '4' }: { tier?: MilharTierId }) {
  const t = useI18nStore((state) => state.t);
  const language = useI18nStore((state) => state.language);
  const setRoute = useAppStore((state) => state.setRoute);
  const goBack = useAppStore((state) => state.goBack);
  const token = useAuthStore((state) => state.token);

  const [tab, setTab] = useState<StatsTab>(DEFAULT_STATS_TAB);
  // 明细是**覆盖层，不是路由** —— 关掉它就回到这张表，标签页、滚动位置、窗口选择
  // 全都还在。走路由的话这三样都要自己存一遍才能还原。
  const [detailRound, setDetailRound] = useState<number | null>(null);
  // round 之外还带上这一行本来就有的 label / result —— 验算页不许自己再猜
  // 这一期是哪一期、开奖号是什么（Finding 4），它俩必须原样从 History 行带过去。
  const [verifyRound, setVerifyRound] = useState<{ round: number; label: string; result: string } | null>(null);
  const fiatCurrency = useCountryStore((state) => state.country.fiatCurrency);
  const countryCode = useCountryStore((state) => state.country.code);
  const [position, setPosition] = useState(1);
  const [window, setWindow] = useState(100);
  const [descending, setDescending] = useState(true);

  const query = useQuery({
    // 国家进 key：下注额那一列现在按观看者的币种统计，游客切国家必须重取，
    // 否则会拿着上一个国家的缓存继续显示。
    queryKey: ['milhar-stats', tier, window, token || 'guest', countryCode],
    queryFn: () => fetchMilharStats(window, token, tier),
    staleTime: 30_000,
  });

  const data = query.data;
  const digits = data?.digits ?? 4;
  const sample = data?.sample ?? 0;
  const band = { low: data?.lowerBound ?? 0, high: data?.upperBound ?? 0 };
  const current = data?.positions.find((p) => p.position === position);
  const values = tab === 'gaps' ? (current?.gaps ?? []) : (current?.counts ?? []);
  const baseRows = values.length ? statsRows(values, tab, band) : [];
  const rows = tab === 'fair' ? baseRows : toggleSorted(baseRows, descending);

  useEffect(() => {
    if (position > digits) setPosition(1);
  }, [digits, position]);

  return (
    <section className="ds-screen milhar-screen">
      <header className="ds-header shrink-0">
        <button className="icon-button tap" type="button"
          // 用 goBack 不用 setRoute：Resultados 现在是常驻按钮，来回点这颗入口
          // 会一直往历史里压 milhar/stats 配对，Android 硬件返回键反而往前跳
          // 进结果页（2026-09-11 复盘 Finding 2，见 appStore.ts 里 goBack 的注释）。
          onClick={() => goBack(tier === '5' ? 'milhar.hora' : 'milhar')} aria-label={t('back')}>
          <ChevronLeft size={20} aria-hidden="true" />
        </button>
        <h1 className="flex-1 text-[20px] leading-[26px] font-extrabold">
          {t('milharResultsPageTitle')}
        </h1>
      </header>

      <div className="milhar-stats-tabs" role="tablist">
        {/* 「我的」对访客不渲染 —— 对他永远是一句登录提示，占着最显眼的第二位
            只会挤窄其余四个标签。 */}
        {STATS_TABS.filter((entry) => entry.key !== 'mine' || !!token).map((entry) => (
          <button key={entry.key} type="button" role="tab"
            aria-selected={tab === entry.key}
            className={`milhar-stats-tab tap${tab === entry.key ? ' is-on' : ''}`}
            onClick={() => setTab(entry.key)}>
            {t(entry.label)}
          </button>
        ))}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar">
        {/* §16 —— 全页禁止整屏 spinner（D4 §4.8），载入用骨架，错误给重试 */}
        {query.isError ? (
          <div className="milhar-stats-fallback">
            <p>{t('milharStatsError')}</p>
            <button type="button" className="milhar-verify tap"
              onClick={() => query.refetch()}>{t('milharStatsRetry')}</button>
          </div>
        ) : query.isPending ? (
          <div className="milhar-stats-skeleton">
            {Array.from({ length: 10 }, (_, i) => <span key={i} className="sk" />)}
          </div>
        ) : tab === 'history' ? (
          <HistoryTable
            rows={data?.history ?? []}
            language={language}
            signedIn={!!token}
            window={data?.window ?? window}
            onOpenRound={setDetailRound}
            onVerifyRound={(round, label, result) => setVerifyRound({ round, label, result })}
          />
        ) : tab === 'mine' ? (
          <MineTable
            token={token}
            tier={tier}
            language={language}
            onOpenRound={setDetailRound}
            onVerifyRound={(round, label, result) => setVerifyRound({ round, label, result })}
          />
        ) : (
          <>
            <div className="milhar-stats-filters">
              <div className="milhar-stats-filter">
                <span className="milhar-stats-filter-label">{t('milharStatsPosition')}</span>
                <div className="milhar-chips" role="group">
                  {Array.from({ length: digits }, (_, i) => i + 1).map((n) => (
                    <button key={n} type="button" className="milhar-chip"
                      aria-pressed={position === n} onClick={() => setPosition(n)}>D{n}</button>
                  ))}
                </div>
              </div>
              <div className="milhar-stats-filter">
                <span className="milhar-stats-filter-label">{t('milharStatsPeriod')}</span>
                <div className="milhar-chips" role="group">
                  {(data?.windows ?? [100, 500, 1000]).map((n) => (
                    <button key={n} type="button" className="milhar-chip"
                      aria-pressed={window === n} onClick={() => setWindow(n)}>{n}</button>
                  ))}
                </div>
              </div>
            </div>

            {tab !== 'fair' ? (
              <button type="button" className="milhar-stats-sort tap"
                onClick={() => setDescending((v) => !v)}>
                {t('milharStatsSample').replace('%n%', String(sample))} {descending ? '↓' : '↑'}
              </button>
            ) : null}

            {sample === 0 ? (
              <p className="milhar-empty">{t('milharStatsEmpty')}</p>
            ) : null}
            <ul className="milhar-stats-list">
              {rows.map((row) => (
                <li key={row.digit} className="milhar-stats-row">
                  <b className="milhar-stats-digit mono">{row.digit}</b>
                  <span className="milhar-stats-bar">
                    <i style={{ width: `${row.barPct}%` }} />
                  </span>
                  <span className="milhar-stats-value mono">
                    {tab === 'gaps' ? gapText(row.value, sample) : row.value}
                  </span>
                  {row.withinBand === null ? null : (
                    <span className={`milhar-stats-band${row.withinBand ? '' : ' is-out'}`}>
                      {row.withinBand
                        ? `✓ ${t('milharStatsNormal')}`
                        : t('milharStatsOutside')}
                    </span>
                  )}
                </li>
              ))}
            </ul>

            {tab === 'fair' && sample > 0 ? (
              <p className="milhar-stats-expected mono">
                {t('milharStatsExpected')
                  .replace('%e%', (data?.expected ?? 0).toFixed(0))
                  .replace('%l%', String(band.low))
                  .replace('%h%', String(band.high))}
              </p>
            ) : null}
          </>
        )}

        {/* §12A.5 —— 事实陈述，不是劝阻。见文件头注释第 3 条 */}
        <p className="milhar-stats-footer">{t('milharStatsFooter')}</p>
      </div>
      {detailRound != null ? (
        <RoundDetailSheet
          round={detailRound}
          currency={fiatCurrency}
          token={token}
          tier={tier}
          onClose={() => setDetailRound(null)}
        />
      ) : null}
      {verifyRound != null ? (
        <MilharVerifyOverlay
          round={verifyRound.round}
          label={verifyRound.label}
          result={verifyRound.result}
          tier={tier}
          onClose={() => setVerifyRound(null)}
        />
      ) : null}
    </section>
  );
}

/**
 * 每一期：期号、开奖号、时间、下注额。
 *
 * <p><b>颜色的边界。</b> 本页顶部那条规则说冷热不用颜色编码 —— 满屏红蓝是路单的
 * 视觉套路，会把统计读成预言。这张表不违反它：颜色在这里说的是「**这是钱 / 这是
 * 号码 / 这是时间**」，是排版；说「这个号码热」才是预言。所以金额走金色（D4 里金
 * 就是钱），号码走中性亮色，时间压暗 —— **没有一处颜色和数值大小挂钩**。
 *
 * <p>隔行底色只为分行，不携带含义。
 */
function HistoryTable({ rows, language, signedIn, window, mineOnly = false, foot, emptyText, onOpenRound, onVerifyRound }: {
  rows: {
    round: number; label: string; result: string; drawnAt: number; staked: string;
    jackpot?: boolean; mine?: boolean; myStaked?: string; myPayout?: string | null;
  }[];
  language: string;
  signedIn: boolean;
  /** 这一次取的期数（100/500/1000）——「最近 %n% 期你没有下注」里的那个数 */
  window: number;
  /** 「我的」页签：每一行都是我下过注的，图例里不画「未参与」那一档 */
  mineOnly?: boolean;
  /** 「我的」页签的翻页脚（骨架 / 重试 / 到底了） */
  foot?: ReactNode;
  emptyText?: string;
  onOpenRound: (round: number) => void;
  onVerifyRound: (round: number, label: string, result: string) => void;
}) {
  const t = useI18nStore((state) => state.t);
  const nowSec = Math.floor(Date.now() / 1000);
  if (rows.length === 0) {
    return <p className="milhar-hist-empty">{emptyText ?? t('milharHistEmpty')}</p>;
  }
  // 竖条列要不要画，只看这一个条件，表头 / 栅格 / 每行 cell 全部从它派生 ——
  // 分头判断的话三处迟早会算出不一样的结果（本次评审 Finding 1 的病根）。
  // 登录了但后端还没上线 mine 字段时也要收起整列，而不是画一排假的「没参与」。
  // 「我的」页签里每一行都是我的，竖条恒有意义（金=中了、绿=没中）。
  const railOn = mineOnly || (signedIn && hasMineData(rows));
  // 爆奖那一行的金底一直都在，只是从来没有一处文字说明它是什么；竖条同理。
  const legend = historyLegendView({
    signedIn, railOn, jackpot: true, mineOnly, rowCount: rows.length, window,
  });
  const groups = groupByLocalDay(rows);
  const oneDay = 86_400;

  function dayLabel(at: number): string {
    if (isSameLocalDay(at, nowSec)) return t('milharHistToday');
    if (isSameLocalDay(at, nowSec - oneDay)) return t('milharHistYesterday');
    return new Date(at * 1000).toLocaleDateString(language, {
      day: '2-digit', month: '2-digit', weekday: 'short',
    });
  }
  return (
    <div className={`milhar-hist${railOn ? '' : ' is-rail-off'}${mineOnly ? ' is-mine' : ''}`}>
      <HistoryLegend items={legend.items} noRecent={legend.noRecent}
        noRecentKey="milharHistNoneRecent" />
      {/* 表头是给眼睛的列标，不是 ARIA 表格 —— 行已经是按钮，把它们同时声明成
          table/row/cell 会让读屏器把一个可点的行念成一个不可点的单元格。 */}
      <div className="milhar-hist-head" aria-hidden="true">
        {/* 每个表头 span 都按类名而非位置认领对齐样式 —— 见 ds-components.css
            里的注释：登录态多一列前导空位，Task 6 又在行尾加验算列，列数
            会变、还会再变，位置选择器迟早再指错一次。 */}
        {railOn ? <span className="milhar-hist-head-rail" /> : null}
        <span className="milhar-hist-head-round">{t('milharHistRound')}</span>
        <span className="milhar-hist-head-result">{t('milharHistResult')}</span>
        <span className="milhar-hist-head-time">{t('milharHistTime')}</span>
        <span className="milhar-hist-head-staked">
          {mineOnly ? t('sorteiosMineHeadPrize') : t('milharHistStaked')}
        </span>
        <span />
      </div>
      {groups.map((group) => (
        // key 由 groupByLocalDay 保证唯一（无时间组可能不止一个，都是就地断出来的）；
        // 是否画分隔条只看 at，与 key 无关。
        <Fragment key={group.key}>
          {/* at 为 0 表示这一组时间不可用 —— 不画分隔条，更不画 1970-01-01 */}
          {group.at > 0 ? (
            <h3 className="milhar-hist-day">{dayLabel(group.at)}</h3>
          ) : null}
          {group.rows.map((row) => {
            const time = historyTimeParts(row.drawnAt);
            return (
              /* 外层降级为 div，行内是两个并列的按钮 —— 按钮不能嵌套按钮，会让
                 键盘和读屏器都到不了内层。主区域跨前四格，盾牌单独一格。 */
              <div
                key={row.round}
                className={`milhar-hist-row${row.jackpot ? ' is-jackpot' : ''}`}
              >
                {railOn ? (
                  <span className={`milhar-hist-rail is-${mineMark(row)}`} aria-hidden="true" />
                ) : null}
                <button
                  type="button"
                  className="milhar-hist-main tap"
                  onClick={() => onOpenRound(row.round)}
                  title={`global #${row.round}`}
                  aria-label={[
                    row.label || `#${row.round}`,
                    row.result,
                    // 图标没了，读屏器就是爆奖唯一的线索（Task 4）
                    row.jackpot ? t('milharHistJackpot') : '',
                    railOn && mineMark(row) === 'won' ? t('milharHistMineWon') : '',
                    railOn && mineMark(row) === 'played' ? t('milharHistMinePlayed') : '',
                  ].filter(Boolean).join(' · ')}
                >
                  <span className="milhar-hist-round">{row.label || `#${row.round}`}</span>
                  <span className="milhar-hist-result">
                    {row.result.split('').map((digit, index) => <b key={index}>{digit}</b>)}
                  </span>
                  {/* 时间缺失就留空 —— 显示 1970 或者当前时间冒充开奖时间都更糟 */}
                  <span className="milhar-hist-time">
                    {time ? time.date.toLocaleTimeString(language, {
                      hour: '2-digit', minute: '2-digit', hour12: false,
                    }) : null}
                  </span>
                  {/* 「我的」页签这一格是**我的**派彩，不是全场下注额 —— 在
                      「我投过注的轮次」里印别人的钱，玩家会以为那是自己赢的。
                      我下了多少在第二行。字段缺席就留空：`Number(undefined)`
                      是 NaN，会渲染成 "R$ NaN"。 */}
                  {mineOnly ? (
                    <span className={`milhar-hist-staked${Number(row.myPayout) > 0 ? '' : ' is-none'}`}>
                      {Number(row.myPayout) > 0 ? fmtMoney(Number(row.myPayout)) : '\u2014'}
                    </span>
                  ) : (
                    <span className="milhar-hist-staked">
                      {Number.isFinite(Number(row.staked)) ? fmtMoney(Number(row.staked)) : null}
                    </span>
                  )}
                  {mineOnly && Number.isFinite(Number(row.myStaked)) ? (
                    <span className="milhar-hist-mystake">
                      {t('milharMineStake').replace('%v%', fmtMoney(Number(row.myStaked)))}
                    </span>
                  ) : null}
                </button>
                <button
                  type="button"
                  className="milhar-hist-verify tap"
                  onClick={() => onVerifyRound(row.round, row.label, row.result)}
                  aria-label={t('milharVerifyOpen').replace('%n%', row.label || `#${row.round}`)}
                >
                  <ShieldCheck size={15} aria-hidden="true" />
                </button>
              </div>
            );
          })}
        </Fragment>
      ))}
      {foot}
    </div>
  );
}

/**
 * 「我的」页签：我下过注的那些轮次，按期号往前翻。
 *
 * <p>History 页签是「最近 N 期」（N 最大 1000），参与与否只是每一行的一个装饰。
 * 玩家要回答的却是反过来的问题：「我 8 月中的那次是哪一期」—— 在一千行里逐行
 * 找竖条不是一个能完成的任务（2026-09-17 PM）。这里只列我下过注的期，不受那
 * 1000 期的窗口限制。
 *
 * <p>行的渲染完全复用 `HistoryTable` —— 两个页签的行必须逐像素相同，各写一遍
 * 迟早会在某一次改动后只跟了一边。
 */
function MineTable({ token, tier, language, onOpenRound, onVerifyRound }: {
  token: string | null;
  tier: MilharTierId;
  language: string;
  onOpenRound: (round: number) => void;
  onVerifyRound: (round: number, label: string, result: string) => void;
}) {
  const t = useI18nStore((state) => state.t);
  const sentinel = useRef<HTMLDivElement | null>(null);
  const query = useInfiniteQuery({
    queryKey: ['milhar-stats-mine', tier, token || 'guest'],
    enabled: !!token,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => fetchMilharMineStats(token, pageParam, tier, 50),
    getNextPageParam: (last) => last.next_cursor || null,
    staleTime: 30_000,
  });

  const pages = query.data?.pages || [];
  const rows: MilharHistoryItem[] = pages.flatMap((page) => page.history || []);
  // 汇总只认第一页：它描述的是全部历史，不是已加载的这几页。
  const summary = pages[0]?.summary || null;

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  useEffect(() => {
    const node = sentinel.current;
    if (!node || !hasNextPage || isFetchingNextPage) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) fetchNextPage();
    }, { rootMargin: '300px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, rows.length]);

  if (!token) return <p className="milhar-hist-empty">{t('milharMineGuest')}</p>;
  if (query.isError && rows.length === 0) {
    return (
      <div className="milhar-stats-fallback">
        <p>{t('milharStatsError')}</p>
        <button type="button" className="milhar-verify tap"
          onClick={() => query.refetch()}>{t('milharStatsRetry')}</button>
      </div>
    );
  }
  if (query.isPending) {
    return (
      <div className="milhar-stats-skeleton">
        {Array.from({ length: 10 }, (_, i) => <span key={i} className="sk" />)}
      </div>
    );
  }

  const foot = (
    <div ref={sentinel} className="srt-mine-more">
      {isFetchingNextPage ? (
        <div className="milhar-stats-skeleton">
          {Array.from({ length: 3 }, (_, i) => <span key={i} className="sk" />)}
        </div>
      ) : query.isError ? (
        <button type="button" className="srt-mine-retry tap"
          onClick={() => fetchNextPage()}>{t('sorteiosMineMoreError')}</button>
      ) : hasNextPage ? null : (
        <span className="srt-mine-end">{t('sorteiosMineEnd')}</span>
      )}
    </div>
  );

  return (
    <>
      {summary ? (
        <p className="srt-mine-summary">
          {t('milharMineSummary')
            .replace('%r%', String(Number(summary.rounds ?? 0)))
            .replace('%w%', String(Number(summary.won ?? 0)))
            .replace('%v%', fmtMoney(Number(summary.payout_total ?? 0)))}
        </p>
      ) : null}
      <HistoryTable
        rows={rows}
        language={language}
        signedIn
        window={rows.length}
        mineOnly
        foot={rows.length ? foot : undefined}
        emptyText={t('milharMineEmpty')}
        onOpenRound={onOpenRound}
        onVerifyRound={onVerifyRound}
      />
    </>
  );
}
